<template>
	<div
		v-if="videos && videos.length > 0"
		class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
		<div
			v-for="video in videos"
			:key="video.id"
			class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
			<div class="h-80 bg-black">
				<iframe
					v-if="getYouTubeVideoId(video.url)"
					:src="`https://www.youtube.com/embed/${getYouTubeVideoId(video.url)}`"
					class="w-full h-full"
					frameborder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					allowfullscreen></iframe>
				<div v-else class="w-full h-full flex items-center justify-center text-gray-400">
					<span>Invalid YouTube URL</span>
				</div>
			</div>

			<div class="p-4">
				<h3 class="font-semibold text-gray-900 mb-2 line-clamp-2">
					{{ video.description || 'Untitled Video' }}
				</h3>

				<a
					:href="formatUrl(video.url)"
					target="_blank"
					rel="noopener noreferrer"
					class="text-sm text-blue-600 hover:underline break-all line-clamp-1 inline-block">
					{{ video.url }}
				</a>

				<div class="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
					<a
						:href="formatUrl(video.url)"
						target="_blank"
						rel="noopener noreferrer"
						class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
						<svg
							class="w-4 h-4 mr-1"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
						</svg>
						Open on YouTube
					</a>

					<app-button
						v-if="canEdit"
						icon="trash"
						theme="danger"
						size="small"
						@click="removeVideo(video)">
					</app-button>
				</div>
			</div>
		</div>
	</div>

	<div v-else class="text-center py-12 text-gray-500">
		<svg
			class="w-16 h-16 mx-auto mb-4 text-gray-300"
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24">
			<path
				stroke-linecap="round"
				stroke-linejoin="round"
				stroke-width="1.5"
				d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
		</svg>
		<p class="text-lg font-medium">No videos added yet</p>
		<p class="text-sm mt-1">Add YouTube videos to showcase this product</p>
	</div>
</template>

<script lang="ts" setup>
	import AppButton from '@/components/base/AppButton.vue'
	import ProductService from '@/services/product'
	import useToaster from '@/stores/toaster'

	interface Video {
		id: string | number
		url: string
		description: string
	}

	const props = defineProps<{
		productId: string | number
		videos: Video[]
		canEdit: boolean
	}>()

	const emit = defineEmits(['video-removed'])

	const toaster = useToaster()

	const formatUrl = (url: string) => {
		if (!url) return '#'
		if (url.startsWith('http://') || url.startsWith('https://')) {
			return url
		}
		return `https://${url}`
	}

	const getYouTubeVideoId = (url: string): string | null => {
		if (!url) return null
		const patterns = [
			/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
			/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
			/(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
			/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/
		]
		for (const pattern of patterns) {
			const match = url.match(pattern)
			if (match) return match[1]
		}
		return null
	}

	const removeVideo = async (video: Video) => {
		if (confirm(`Are you sure you want to remove the video "${video.description}"?`)) {
			try {
				const api = new ProductService()
				await api.removeVideo(props.productId, video.id)
				toaster.success({ text: 'Video link removed successfully!' })
				emit('video-removed')
			} catch (error) {
				console.error('Error removing video link:', error)
				toaster.error({ text: 'Failed to remove video link.' })
			}
		}
	}
</script>
